import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Calendar, Mail, Phone, MapPin } from 'lucide-react';
import SEO from '../components/SEO';
import { useLocalization } from '../hooks/useLocalization';

const TermsOfServicePage: React.FC = () => {
  const { t } = useLocalization();
  const lastUpdated = "December 31, 2024";

  return (
    <div className="min-h-screen bg-gray-50">
      <SEO
        title="Terms of Service"
        description="Evoltbay Terms of Service. Terms governing use of our solar energy marketplace, enterprise quotation system, and government solar contracting platform."
        keywords="terms of service, Evoltbay terms, solar marketplace terms, enterprise quotation terms"
        url="https://evoltbay.com/terms"
      />
      {/* Header */}
      <div className="bg-white shadow-sm border-b">
        <div className="max-w-4xl mx-auto px-4 py-6">
          <div className="flex items-center gap-4 mb-4">
            <Link 
              to="/" 
              className="flex items-center gap-2 text-blue-600 hover:text-blue-800 transition-colors"
            >
              <ArrowLeft size={20} />
              {t('terms.backToEvoltbay')}
            </Link>
          </div>
          <h1 className="text-3xl font-bold text-gray-900">{t('terms.title')}</h1>
          <div className="flex items-center gap-2 text-gray-600 mt-2">
            <Calendar size={16} />
            <span>{t('terms.lastUpdated')}: {lastUpdated}</span>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-4xl mx-auto px-4 py-8">
        <div className="bg-white rounded-lg shadow-sm p-8 space-y-8">
          
          {/* Introduction */}
          <section>
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">1. Introduction</h2>
            <p className="text-gray-700 leading-relaxed mb-4">
              Welcome to Evoltbay, a comprehensive solar energy marketplace platform operated by Evoltbay Inc. ("Evoltbay," "we," "us," or "our"). 
              These Terms of Service ("Terms") govern your use of our website, mobile application, and related services (collectively, the "Service").
            </p>
            <p className="text-gray-700 leading-relaxed">
              By accessing or using Evoltbay, you agree to be bound by these Terms. If you disagree with any part of these Terms, 
              then you may not access the Service.
            </p>
          </section>

          {/* Definitions */}
          <section>
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">2. Definitions</h2>
            <div className="space-y-3">
              <div>
                <strong className="text-gray-900">"Platform"</strong> - The Evoltbay website, mobile applications, and related services.
              </div>
              <div>
                <strong className="text-gray-900">"Users"</strong> - All individuals and entities using the Platform, including Buyers and Vendors.
              </div>
              <div>
                <strong className="text-gray-900">"Buyers"</strong> - Users seeking to purchase solar energy products and services.
              </div>
              <div>
                <strong className="text-gray-900">"Vendors"</strong> - Users offering solar energy products and services for sale.
              </div>
              <div>
                <strong className="text-gray-900">"Enterprise Users"</strong> - Business entities using advanced features for bulk purchases or sales.
              </div>
              <div>
                <strong className="text-gray-900">"Government Agencies"</strong> - Public sector entities using specialized procurement features.
              </div>
            </div>
          </section>

          {/* User Accounts */}
          <section>
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">3. User Accounts</h2>
            <h3 className="text-xl font-medium text-gray-900 mb-3">3.1 Account Creation</h3>
            <p className="text-gray-700 leading-relaxed mb-4">
              To access certain features of the Service, you must create an account. You agree to provide accurate, current, 
              and complete information during registration and to update such information to keep it accurate, current, and complete.
            </p>
            
            <h3 className="text-xl font-medium text-gray-900 mb-3">3.2 Account Security</h3>
            <p className="text-gray-700 leading-relaxed mb-4">
              You are responsible for safeguarding the password and for all activities that occur under your account. 
              You agree to immediately notify Evoltbay of any unauthorized use of your account.
            </p>

            <h3 className="text-xl font-medium text-gray-900 mb-3">3.3 Account Types</h3>
            <ul className="list-disc list-inside text-gray-700 space-y-2">
              <li>Regular Users: Individual buyers and vendors</li>
              <li>Enterprise Users: Business entities with enhanced features</li>
              <li>Government Agencies: Public sector entities with specialized procurement tools</li>
            </ul>
          </section>

          {/* Platform Usage */}
          <section>
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">4. Platform Usage</h2>
            <h3 className="text-xl font-medium text-gray-900 mb-3">4.1 Permitted Use</h3>
            <p className="text-gray-700 leading-relaxed mb-4">
              Evoltbay provides a marketplace for solar energy products and services. You may use the Platform to:
            </p>
            <ul className="list-disc list-inside text-gray-700 space-y-2 mb-4">
              <li>Browse and purchase solar energy products</li>
              <li>List and sell solar energy products (if you are a verified vendor)</li>
              <li>Participate in auctions and bidding processes</li>
              <li>Request and receive quotes for solar installations</li>
              <li>Use ROI simulation tools</li>
              <li>Manage your digital wallet and transactions</li>
            </ul>

            <h3 className="text-xl font-medium text-gray-900 mb-3">4.2 Prohibited Activities</h3>
            <p className="text-gray-700 leading-relaxed mb-2">You agree not to:</p>
            <ul className="list-disc list-inside text-gray-700 space-y-2">
              <li>Use the Platform for any illegal or unauthorized purpose</li>
              <li>Violate any applicable laws or regulations</li>
              <li>Infringe upon the rights of others</li>
              <li>Upload or transmit malicious code or harmful content</li>
              <li>Attempt to gain unauthorized access to the Platform</li>
              <li>Engage in fraudulent activities or misrepresent products/services</li>
              <li>Spam or harass other users</li>
            </ul>
          </section>

          {/* Vendor Responsibilities */}
          <section>
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">5. Vendor Responsibilities</h2>
            <h3 className="text-xl font-medium text-gray-900 mb-3">5.1 Product Listings</h3>
            <p className="text-gray-700 leading-relaxed mb-4">
              Vendors must provide accurate, complete, and up-to-date information about their products and services. 
              All listings must comply with applicable laws and industry standards.
            </p>

            <h3 className="text-xl font-medium text-gray-900 mb-3">5.2 Licensing and Compliance</h3>
            <p className="text-gray-700 leading-relaxed mb-4">
              Vendors must maintain all necessary licenses, certifications, and insurance required for their business operations. 
              Government vendors must meet additional certification requirements for public sector contracts.
            </p>

            <h3 className="text-xl font-medium text-gray-900 mb-3">5.3 Order Fulfillment</h3>
            <p className="text-gray-700 leading-relaxed">
              Vendors are responsible for timely fulfillment of orders, proper packaging, shipping, and customer service 
              related to their products and services.
            </p>
          </section>

          {/* Payments and Transactions */}
          <section>
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">6. Payments and Transactions</h2>
            <h3 className="text-xl font-medium text-gray-900 mb-3">6.1 Payment Processing</h3>
            <p className="text-gray-700 leading-relaxed mb-4">
              Evoltbay uses third-party payment processors including Stripe for secure payment processing. 
              By using our payment services, you agree to the terms of our payment processors.
            </p>

            <h3 className="text-xl font-medium text-gray-900 mb-3">6.2 Digital Wallet</h3>
            <p className="text-gray-700 leading-relaxed mb-4">
              Users may maintain a digital wallet on the Platform for convenient transactions. 
              Wallet funds are held securely and may be used for purchases within the Platform.
            </p>

            <h3 className="text-xl font-medium text-gray-900 mb-3">6.3 Fees and Charges</h3>
            <p className="text-gray-700 leading-relaxed mb-4">
              Evoltbay may charge platform fees, transaction fees, and other charges as disclosed during the transaction process. 
              All fees are non-refundable unless otherwise specified.
            </p>

            <h3 className="text-xl font-medium text-gray-900 mb-3">6.4 Refunds and Disputes</h3>
            <p className="text-gray-700 leading-relaxed">
              Refund policies are determined by individual vendors. Evoltbay provides dispute resolution services 
              to help resolve conflicts between buyers and vendors.
            </p>
          </section>

          {/* Intellectual Property */}
          <section>
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">7. Intellectual Property</h2>
            <p className="text-gray-700 leading-relaxed mb-4">
              The Platform and its original content, features, and functionality are owned by Evoltbay and are protected by 
              international copyright, trademark, patent, trade secret, and other intellectual property laws.
            </p>
            <p className="text-gray-700 leading-relaxed">
              Users retain ownership of content they upload but grant Evoltbay a license to use such content 
              for Platform operations and marketing purposes.
            </p>
          </section>

          {/* Privacy and Data Protection */}
          <section>
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">8. Privacy and Data Protection</h2>
            <p className="text-gray-700 leading-relaxed mb-4">
              Your privacy is important to us. Our Privacy Policy explains how we collect, use, and protect your information. 
              By using the Platform, you consent to the collection and use of information as described in our Privacy Policy.
            </p>
            <p className="text-gray-700 leading-relaxed">
              <Link to="/privacy" className="text-blue-600 hover:text-blue-800 underline">
                View our Privacy Policy
              </Link>
            </p>
          </section>

          {/* Disclaimers */}
          <section>
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">9. Disclaimers</h2>
            <p className="text-gray-700 leading-relaxed mb-4">
              THE PLATFORM IS PROVIDED ON AN "AS IS" AND "AS AVAILABLE" BASIS. EVOLTBAY MAKES NO WARRANTIES, 
              EXPRESS OR IMPLIED, INCLUDING BUT NOT LIMITED TO IMPLIED WARRANTIES OF MERCHANTABILITY, 
              FITNESS FOR A PARTICULAR PURPOSE, AND NON-INFRINGEMENT.
            </p>
            <p className="text-gray-700 leading-relaxed">
              Evoltbay does not warrant that the Platform will be uninterrupted, secure, or error-free, 
              or that defects will be corrected.
            </p>
          </section>

          {/* Limitation of Liability */}
          <section>
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">10. Limitation of Liability</h2>
            <p className="text-gray-700 leading-relaxed mb-4">
              IN NO EVENT SHALL EVOLTBAY BE LIABLE FOR ANY INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL, 
              OR PUNITIVE DAMAGES, INCLUDING WITHOUT LIMITATION, LOSS OF PROFITS, DATA, USE, GOODWILL, 
              OR OTHER INTANGIBLE LOSSES.
            </p>
            <p className="text-gray-700 leading-relaxed">
              Evoltbay's total liability shall not exceed the amount paid by you to Evoltbay in the 
              twelve (12) months preceding the claim.
            </p>
          </section>

          {/* Termination */}
          <section>
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">11. Termination</h2>
            <p className="text-gray-700 leading-relaxed mb-4">
              We may terminate or suspend your account and access to the Platform immediately, without prior notice, 
              for any reason, including breach of these Terms.
            </p>
            <p className="text-gray-700 leading-relaxed">
              Upon termination, your right to use the Platform will cease immediately, but provisions that 
              by their nature should survive termination will remain in effect.
            </p>
          </section>

          {/* Governing Law */}
          <section>
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">12. Governing Law</h2>
            <p className="text-gray-700 leading-relaxed">
              These Terms shall be governed by and construed in accordance with the laws of [State/Country], 
              without regard to its conflict of law provisions. Any disputes shall be resolved in the courts of [Jurisdiction].
            </p>
          </section>

          {/* Changes to Terms */}
          <section>
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">13. Changes to Terms</h2>
            <p className="text-gray-700 leading-relaxed mb-4">
              We reserve the right to modify these Terms at any time. We will notify users of material changes 
              via email or platform notification. Continued use of the Platform after changes constitutes acceptance.
            </p>
          </section>

          {/* Contact Information */}
          <section>
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">14. Contact Information</h2>
            <p className="text-gray-700 leading-relaxed mb-4">
              If you have any questions about these Terms, please contact us:
            </p>
            <div className="space-y-2 text-gray-700">
              <div className="flex items-center gap-2">
                <Mail size={16} />
                <span>legal@evoltbay.com</span>
              </div>
              <div className="flex items-center gap-2">
                <Phone size={16} />
                <span>+1 (555) 123-4567</span>
              </div>
              <div className="flex items-center gap-2">
                <MapPin size={16} />
                <span>123 Solar Street, Green City, GC 12345</span>
              </div>
            </div>
          </section>

          {/* Footer */}
          <div className="border-t pt-6 mt-8">
            <p className="text-sm text-gray-500 text-center">
              © 2024 Evoltbay Inc. All rights reserved. | 
              <Link to="/privacy" className="text-blue-600 hover:text-blue-800 ml-1">Privacy Policy</Link>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TermsOfServicePage; 